import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import QuestionShort from "../../../profile/components/QuestionShort";
import QuestionMultiple from "../../../profile/components/QuestionMultiple";
import QuestionScale from "../../../profile/components/QuestionScale";
import BodyWeightStats from "../../../calendar/body-composition/components/BodyWeightStats";
import { ProgressPictureAnswer } from "../../../profile/components/QuestionProgressPictures";
import NutritionHistoryTable from "../../../nutrition/components/NutritionHistoryTable";
import { getSingleAverageStat } from "../../../../../utils/nutrition-helpers";
import SummaryPersonalStats from "./SummaryPersonalStats";
import SummaryVideoList from "./SummaryVideoList";
import { frequencyLimit } from "../../../../../utils/questionnaire";
import VideoRecordComponent from "../../../../../parts/VideoRecordComponent";
import {
  DATE_FORMATS,
  defaultFirebaseProjectId,
} from "../../../../../constants";
import Avatar from "../../../../../parts/avatar";
import SvgIcon from "../../../../../parts/SvgIcon";
import submit from "submit-form";
const { DATE_DAY_FORMAT, UTC } = DATE_FORMATS;
import SummaryDailyStats from "./SummaryDailyStats";
import BodyMeasureStats from "../../../calendar/body-composition/components/BodyMeasureStats";
import BarGraph from "../../../calendar/body-composition/components/BarGraph";
import { determineAvg } from "../../../calendar/body-composition/body-composition.helper";
import SummaryMenstrualCycle from "./SummaryMenstrualCycle";

const DisplaySummaryQuestions = ({
  answerStats,
  dailyStats,
  threeMonthData,
  athlete,
  date,
  units,
  nutritionPlans,
  dayPersonalRecords,
  locals,
  query,
  activeExercises,
}) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const organizationCoach = query.organizationCoach || undefined;
  const organizationCoachParam = organizationCoach
    ? `&organizationCoach=${organizationCoach}`
    : "";
  const { questionnaire, utcDate } = answerStats;
  const { questionnaireFrequency } = questionnaire;
  // This is done because the answers are stored in the order they are answered in
  // It is also necessary to include the slice() as reverse() mutates the original array
  const reversedAnswers = questionnaire.questionnaireAnswers.slice().reverse();
  const timePeriod = frequencyLimit[questionnaireFrequency];

  const end = moment(utcDate, "YYYYMMDD").format("YYYY-MM-DD");
  const start = moment(utcDate, "YYYYMMDD")
    .subtract(timePeriod - 1, "days")
    .format("YYYY-MM-DD");

  // Current check-in date, shown beside the "current" progress photos to mirror
  // the previous check-in date.
  const currentCheckInDate = moment(utcDate, "YYYYMMDD").format("MMM D, YYYY");

  // Previous check-in: most recent stat with a questionnaire before this one,
  // used to compare progress photos against the current check-in.
  const previousCheckIn = dailyStats
    .filter((stat) => !!stat.questionnaire && stat.utcDate < utcDate)
    .reduce((a, b) => (!a || b.utcDate > a.utcDate ? b : a), null);

  let previousFilteredStats = [];
  let previousProgressAnswer = null;
  let previousCheckInDate = null;
  if (previousCheckIn) {
    const prevTimePeriod =
      frequencyLimit[previousCheckIn.questionnaire.questionnaireFrequency] || 7;
    const prevStart = Number(
      moment(previousCheckIn.utcDate, "YYYYMMDD")
        .subtract(prevTimePeriod - 1, "days")
        .format("YYYYMMDD")
    );
    previousFilteredStats = dailyStats.filter(
      (stat) =>
        stat.utcDate >= prevStart && stat.utcDate <= previousCheckIn.utcDate
    );
    previousProgressAnswer = previousCheckIn.questionnaire.questionnaireAnswers.find(
      (a) => a.type === "progress"
    );
    previousCheckInDate = moment(previousCheckIn.utcDate, "YYYYMMDD").format(
      "MMM D, YYYY"
    );
  }

  // Stats in the date range of the check in period
  const filteredStats = dailyStats
    .filter(
      (stat) =>
        stat.utcDate >= Number(moment(start).format("YYYYMMDD")) &&
        stat.utcDate <= Number(moment(end).format("YYYYMMDD"))
    )
    .slice()
    .sort((a, b) => b.utcDate - a.utcDate);

  // So we can implement the rest day on the check in nutrition table
  const statsWithoutWeekAverage = filteredStats.map((stat) => {
    const exercise = activeExercises.find((ex) => ex.utcDate === stat.utcDate);
    stat.hasWorkout = !!exercise;
    return stat;
  });

  // Average stats for the nutrition table, it's a single average that includes all the values from that period of time
  const averageStats = getSingleAverageStat(start, end, dailyStats);
  // Combines both arrays of data for the nutrition table, the average will always be displayed first
  const nutritionHistory = statsWithoutWeekAverage
    .concat(averageStats)
    .sort((a, b) => b.utcDate - a.utcDate);

  return (
    <div className="Check-in-Questionnaire">
      {reversedAnswers.map((answer) => (
        <DisplaySingleQuestion
          singleAnswer={answer}
          key={answer._id}
          dailyStats={filteredStats}
          athlete={athlete}
          date={date}
          threeMonthData={threeMonthData}
          start={start}
          end={end}
          units={units}
          timePeriod={timePeriod}
          nutritionPlans={nutritionPlans}
          nutritionHistory={nutritionHistory}
          organizationCoachParam={organizationCoachParam}
          activeExercises={activeExercises}
          previousDailyStats={previousFilteredStats}
          previousProgressAnswer={previousProgressAnswer}
          previousCheckInDate={previousCheckInDate}
          currentCheckInDate={currentCheckInDate}
          t={t}
        />
      ))}

      {/* Menstrual Cycle */}
      {reversedAnswers.some(
        (answer) => answer.type === "femaleCycleCalendar"
      ) && (
        <SummaryMenstrualCycle
          start={start}
          end={end}
          locals={locals}
          dateForUrl={date}
          athlete={athlete}
          organizationCoachParam={organizationCoachParam}
        />
      )}

      {/* Records, Workload and Video List */}
      <SummaryPersonalStats
        start={start}
        end={end}
        units={units}
        dayPersonalRecords={dayPersonalRecords}
      />

      <SummaryDailyStats
        dailyStats={filteredStats}
        date={date}
        athlete={athlete}
        organizationCoachParam={organizationCoachParam}
      />

      <SummaryVideoList
        key={`${date}-${athlete._id}-video-list`}
        start={start}
        end={end}
        date={date}
        locals={locals}
        query={query}
        athlete={athlete}
        organizationCoachParam={organizationCoachParam}
      />

      <footer key={`${date}-${athlete._id}-check-in-video-loom`}>
        <h4>{t("athlete-portal:checkinSummaryComponents.coachReview")}</h4>

        <div>
          <VideoRecordComponent
            date={moment().format(DATE_DAY_FORMAT)}
            unformattedDate={moment(date, UTC)}
            videoRecordEmbed={
              locals.videoRecord ? locals.videoRecord.embedUrl : null
            }
            locals={locals}
            type={"check-in"}
            reportDate={moment(date, UTC).format(DATE_DAY_FORMAT)}
          />
          <CheckInNotes locals={locals} checkInDate={date} />
        </div>
      </footer>
    </div>
  );
};

/** Daily series saved on the answer, or `{ value: rows[] }` from the API. */
const normalizeRestingHeartRateSeries = (answerValue) => {
  if (Array.isArray(answerValue)) {
    return answerValue;
  }
  if (
    answerValue &&
    typeof answerValue === "object" &&
    Array.isArray(answerValue.value)
  ) {
    return answerValue.value;
  }
  return null;
};

const RestingHeartRateCheckInSummary = ({ rows, t }) => {
  const chartRows = rows.filter(
    (r) =>
      r != null && r.restingHeartRate != null && Number(r.restingHeartRate) > 0
  );
  const average = determineAvg(chartRows, "restingHeartRate");

  const sourceLabels = {
    wearable: t("athlete-portal:restingHeartRate.sourceWearable"),
    manual: t("athlete-portal:restingHeartRate.sourceManual"),
  };

  const tableRows = rows
    .filter((r) => r != null && r.restingHeartRate != null)
    .slice()
    .sort((a, b) => Number(b.utcDate) - Number(a.utcDate));

  const formatSource = (src) => sourceLabels[src] ?? (src ? String(src) : "—");

  const displayDate = (r) =>
    r.date && String(r.date).trim().length
      ? r.date
      : r.utcDate != null
        ? moment(String(r.utcDate), "YYYYMMDD").format("MMM D, YYYY")
        : "";

  return (
    <section className="summary-personal-stats">
      <div className="resting-hr-checkin-summary bg-(--white-05) rounded-(--border-radius) p-6 w-1/2">
        {chartRows.length ? (
          <div
            className="adherence-chart-new resting-hr-checkin-summary-chart"
            style={{ width: "100%", height: 250 }}
          >
            <BarGraph
              data={chartRows}
              graphId="checkin-resting-hr-graph"
              targetValue="restingHeartRate"
              average={average}
              type="Line"
              units={t("athlete-portal:restingHeartRate.unit")}
              tooltipLabel={t("athlete-portal:restingHeartRate.axisLabel")}
            />
          </div>
        ) : (
          <p className="p4 opacity-80">
            {t("athlete-portal:restingHeartRate.noData")}
          </p>
        )}

        {tableRows.length ? (
          <div className="mt-4 resting-hr-checkin-summary-table-wrap overflow-x-auto">
            <section role="table-wrapper" data-borders-inside>
              <table
                role="report-table"
                className="w-full border-collapse text-sm"
              >
                <thead>
                  <tr>
                    <th scope="col">{t("athlete-portal:reports.date")}</th>
                    <th scope="col">
                      {t("athlete-portal:restingHeartRate.unit")}
                    </th>
                    <th scope="col">
                      {t("athlete-portal:restingHeartRate.sourceColumn")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, idx) => (
                    <tr key={row.utcDate != null ? String(row.utcDate) : idx}>
                      <td>{displayDate(row)}</td>
                      <td>{row.restingHeartRate}</td>
                      <td>{formatSource(row.restingHeartRateSource)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </div>
        ) : null}
      </div>
    </section>
  );
};

const CheckInNotes = ({ locals, checkInDate }) => {
  const { t } = useTranslation(["athlete-portal", "common"]);
  const {
    workoutCoachNote,
    workoutNote,
    activeUserToRequest,
    athlete,
    coachOrganizationInformation,
    query,
    activeUser,
  } = locals;

  const [openQuickEditCoachNotes, setOpenQuickEditCoachNotes] = useState(false);
  const [coachNote, setCoachNote] = useState(
    workoutCoachNote?.note?.note || ""
  );
  const athleteNote = workoutNote?.note?.note || "";

  const handleCoachNoteChange = (value) => {
    setCoachNote(value);
  };

  const saveQuickEditCoachNotes = () => {
    submit("/actions/exercise/quick-edit-coach-note", {
      method: "POST",
      body: {
        note: coachNote,
        date: moment(checkInDate, UTC).format(DATE_DAY_FORMAT),
        user: athlete._id,
        coach: query.organizationCoach || activeUser._id,
        avatar:
          !!activeUser.avatar && !!activeUser.avatar.file
            ? activeUser.avatar.file
            : null,
        firebaseProjectId: !!activeUserToRequest.firebaseProjectId
          ? activeUserToRequest.firebaseProjectId
          : defaultFirebaseProjectId,
        type: "check-in",
      },
    });

    // Idk lol
    if (
      locals.workoutCoachNote &&
      locals.workoutCoachNote.note &&
      locals.workoutCoachNote.note.note
    ) {
      locals.workoutCoachNote.note.note = coachNote;
    } else {
      locals.workoutCoachNote = {
        note: { note: coachNote },
      };
    }

    setOpenQuickEditCoachNotes(false);
  };

  const closeQuickEditCoachNotes = () => {
    // So it goes back to it's previous state
    setCoachNote(workoutCoachNote?.note?.note || "");
    setOpenQuickEditCoachNotes(false);
  };

  return (
    <div className="notes-container">
      <div className="coach-notesx">
        <label>{t("athlete-portal:checkinSummaryComponents.notes")}</label>
        <>
          {!openQuickEditCoachNotes ? (
            <div
              className="edit-mode"
              onClick={() => {
                setOpenQuickEditCoachNotes(true);
              }}
            >
              <div className="only-show-mobile">
                {coachNote.trim().length ? (
                  <Avatar
                    size={32}
                    className={`note-avatar only-show-mobile`}
                    user={coachOrganizationInformation}
                  />
                ) : null}
                <div>
                  {coachNote.trim().length ? (
                    <span className={`coach-name`}>
                      {athlete && athlete.coach && athlete.coach.name}
                    </span>
                  ) : null}
                  <span className="click-add">
                    {coachNote.trim().length
                      ? coachNote
                      : t(
                          "athlete-portal:checkinSummaryComponents.tapToAddNote"
                        )}
                  </span>
                </div>
              </div>
              <span className="mobile-hidden">
                {coachNote.trim().length
                  ? coachNote
                  : t(
                      "athlete-portal:checkinSummaryComponents.clickToAddEditNotes"
                    )}
              </span>
            </div>
          ) : (
            <div className="edit-mode editing">
              <textarea
                value={coachNote || ""}
                onChange={({ target: { value } }) =>
                  handleCoachNoteChange(value)
                }
                id="coach-notes-textarea"
                autoFocus={true}
              />
              <div className="button-container">
                <button
                  data-button-type="primary"
                  onClick={() => saveQuickEditCoachNotes()}
                >
                  {t("common:save")}
                </button>
                <button
                  data-button-type="secondary"
                  onClick={() => closeQuickEditCoachNotes()}
                >
                  <SvgIcon
                    src="/img/new-icons/red-cross.svg"
                    noFilterOnLight={true}
                  />
                </button>
              </div>
            </div>
          )}
        </>
      </div>
      {athleteNote && athleteNote.trim().length ? (
        <div className="athlete-notes">
          <label>
            {activeUser.referAsClients
              ? t("athlete-portal:calendarDay.clientNotes")
              : t("athlete-portal:calendarDay.athleteNotes")}
          </label>
          <div className="edit-mode cursor-default!">
            <span className={`athlete-name only-show-mobile`}>
              {athlete && athlete.name}
            </span>
            <span>{athleteNote}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const DisplaySingleQuestion = ({
  singleAnswer,
  dailyStats,
  threeMonthData,
  athlete,
  date,
  start,
  end,
  units,
  timePeriod,
  nutritionPlans,
  nutritionHistory,
  organizationCoachParam,
  activeExercises,
  previousDailyStats,
  previousProgressAnswer,
  previousCheckInDate,
  currentCheckInDate,
  t,
}) => {
  const { type, answer, questionBody, questionTitle } = singleAnswer;

  // Cuz we are already showing these without the need for a question
  if (
    type === "sleep" ||
    type === "steps" ||
    type === "hrv" ||
    type === "femaleCycle" ||
    type === "femaleCycleCalendar"
  )
    return null;

  const questionDisplayTitle = getQuestionTitle(type, questionTitle, t);

  const questionDisplayBody = getQuestionBody(
    type,
    questionBody,
    answer,
    {
      dailyStats,
      threeMonthData,
      athlete,
      date,
      start,
      end,
      units,
      timePeriod,
      nutritionPlans,
      nutritionHistory,
      activeExercises,
      previousDailyStats,
      previousProgressAnswer,
      previousCheckInDate,
      currentCheckInDate,
    },
    organizationCoachParam,
    t
  );

  return (
    <div className={"questionnaire-question"}>
      {type !== "measurement" && (
        <div className="question-header">
          <h4>{questionDisplayTitle}</h4>
          {type === "progress" && (
            <a
              href={`/athlete-portal/metrics/progress-photos?date=${date}&athlete=${athlete._id}&bodycomp=true${organizationCoachParam}`}
              className="composition-see-more"
            >
              <span>{t("athlete-portal:checkinSummaryComponents.seeMore")}</span>
              <SvgIcon
                noFilterOnLight={true}
                src="/img/new-icons/arrow-square-right-gradient.svg"
              />
            </a>
          )}
        </div>
      )}
      <div className="question-body">{questionDisplayBody}</div>
    </div>
  );
};

/** @param {unknown} method API value e.g. "dexa", "scale", "calipers" */
const getBodyFatMethodLabel = (method, t) => {
  if (method == null || method === "") {
    return null;
  }
  const key = String(method).toLowerCase().trim();
  const i18nByMethod = {
    dexa: "athlete-portal:bodyFat.methodDexa",
    scale: "athlete-portal:bodyFat.methodScale",
    calipers: "athlete-portal:bodyFat.methodCalipers",
  };
  const i18nKey = i18nByMethod[key];
  return i18nKey ? t(i18nKey) : String(method);
};

const getQuestionTitle = (type, questionTile, t) => {
  switch (type) {
    case "short":
    case "multiple":
    case "checkbox":
    case "scale":
    case "measurement":
    case "bodyFatPercentage":
    case "restingHeartRate":
      return questionTile;
    case "bodyweight":
      return "";
    case "nutrition":
      return t("athlete-portal:checkinSummaryComponents.nutritionSummary");
    case "progress":
      return t("athlete-portal:checkinSummaryComponents.progressPhotos");
  }
};

const getQuestionBody = (
  type,
  body,
  answer,
  data,
  organizationCoachParam,
  t
) => {
  const {
    dailyStats,
    threeMonthData,
    athlete,
    date,
    start,
    end,
    units,
    timePeriod,
    nutritionPlans,
    nutritionHistory,
    activeExercises,
    previousDailyStats,
    previousProgressAnswer,
    previousCheckInDate,
    currentCheckInDate,
  } = data;

  if (!answer && type !== "progress") {
    return (
      <div>
        {t("athlete-portal:checkinSummaryComponents.errorDisplayingQuestion")}
      </div>
    );
  }

  // Could be converted to a dictionary, but there are too many different attributes in the components so I leave it like this for now
  switch (type) {
    case "short":
      return <QuestionShort description={answer.value} />;
    case "multiple":
    case "checkbox":
      return (
        <QuestionMultiple
          type={body.type}
          options={body.options}
          answers={answer.value}
        />
      );
    case "scale":
      return (
        <QuestionScale
          labelLeft={body.labelLeft}
          labelRight={body.labelRight}
          answer={answer.value}
        />
      );
    case "bodyweight":
      return (
        <BodyWeightStats
          dailyStats={dailyStats}
          athlete={athlete._id}
          date={date}
          threeMonthData={threeMonthData}
          start={start}
          end={end}
          units={units}
          timePeriod={timePeriod}
          organizationCoachParam={organizationCoachParam}
          fromCheckIn={true}
        />
      );
    case "progress":
      return (
        <ProgressPictureAnswer
          answer={answer}
          dailyStats={dailyStats}
          previousAnswer={previousProgressAnswer}
          previousDailyStats={previousDailyStats}
          previousDate={previousCheckInDate}
          currentDate={currentCheckInDate}
        />
      );
    case "nutrition":
      return (
        <NutritionHistoryTable
          units={units}
          nutritionHistory={nutritionHistory}
          nutritionPlans={nutritionPlans}
          isCheckIn={true}
          activeExercises={activeExercises}
        />
      );
    case "measurement":
      return (
        <BodyMeasureStats
          data={threeMonthData}
          athlete={athlete._id}
          date={date}
          units={units}
          organizationCoachParam={organizationCoachParam}
          useLastMeasure={true}
        />
      );

    case "bodyFatPercentage": {
      const raw = answer.value;
      const valueObj =
        raw && typeof raw === "object"
          ? raw
          : { bodyFatPercentage: raw, bodyFatMethod: null };
      const pct = valueObj.bodyFatPercentage;
      const methodLabel = getBodyFatMethodLabel(valueObj.bodyFatMethod, t);
      const pctPart =
        pct != null && pct !== ""
          ? `${pct} %`
          : t(
              "athlete-portal:checkinSummaryComponents.errorDisplayingQuestion"
            );
      const description =
        methodLabel != null && methodLabel !== ""
          ? `${pctPart} — ${methodLabel}`
          : pctPart;
      return <QuestionShort description={description} />;
    }
    case "restingHeartRate": {
      const series = normalizeRestingHeartRateSeries(answer.value);
      if (series && series.length > 0) {
        return <RestingHeartRateCheckInSummary rows={series} t={t} />;
      }
      const raw = answer.value;
      if (
        raw &&
        typeof raw === "object" &&
        !Array.isArray(raw) &&
        raw.restingHeartRate != null
      ) {
        return (
          <QuestionShort
            description={`${raw.restingHeartRate} ${t("athlete-portal:restingHeartRate.unit")}`}
          />
        );
      }
      return (
        <div>
          {t("athlete-portal:checkinSummaryComponents.errorDisplayingQuestion")}
        </div>
      );
    }
    default:
      return (
        <div>
          {t("athlete-portal:checkinSummaryComponents.errorDisplayingQuestion")}
        </div>
      );
  }
};

export default DisplaySummaryQuestions;
